/**
 * Utilitário para validação de horários de acesso (access_time)
 */

/**
 * Converte string no formato "HH:mm" em minutos desde o início do dia.
 * @param {string} timeStr - Ex: "08:30"
 * @returns {number|null} Minutos ou null se inválido
 */
export function timeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null
  const parts = timeStr.trim().split(':')
  if (parts.length < 2) return null

  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)

  if (isNaN(hours) || isNaN(minutes)) return null
  return hours * 60 + minutes
}

/**
 * Valida se a data/hora fornecida está dentro dos limites de access_time.
 * 
 * @param {object|null} accessTime - Objeto access_time do usuário/departamento/empresa
 * @param {Date} [currentDate=new Date()] - Data a ser validada
 * @returns {boolean} true se permitido ou se não houver restrição, false se fora do horário
 */
function hasConfiguredSlot(slot) {
  if (!slot || !slot.start || !slot.end) return false
  return timeToMinutes(slot.start) !== null && timeToMinutes(slot.end) !== null
}

function hasAnyConfiguredHours(hoursConfig) {
  if (!Array.isArray(hoursConfig)) return false
  return hoursConfig.some(day => Array.isArray(day?.hours) && day.hours.some(hasConfiguredSlot))
}

export function isAccessTimeAllowed(accessTime, currentDate = new Date()) {
  if (!accessTime) return true

  // Tipo ilimitado explícito
  if (accessTime.type === 'unlimited') return true

  const hoursConfig = accessTime.hours
  if (!Array.isArray(hoursConfig) || hoursConfig.length === 0) {
    // Sem regras de horário cadastradas
    return true
  }

  // Template vazio (todos start/end null) = horário da empresa não configurado.
  // O check_access libera (allowed: true); bloquear aqui impede entrada na v2.
  if (!hasAnyConfiguredHours(hoursConfig)) {
    return accessTime.allowed !== false
  }

  // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
  const currentDay = currentDate.getDay()
  const daySchedule = hoursConfig.find(item => item.day === currentDay)

  // Se não existe configuração para o dia de hoje, ou lista de horários vazia, acesso bloqueado
  if (!daySchedule || !Array.isArray(daySchedule.hours) || daySchedule.hours.length === 0) {
    return false
  }

  const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes()

  // Verifica se há pelo menos um intervalo válido cadastrado para o dia
  for (const slot of daySchedule.hours) {
    if (!hasConfiguredSlot(slot)) continue

    const startMinutes = timeToMinutes(slot.start)
    const endMinutes = timeToMinutes(slot.end)

    if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
      return true
    }
  }

  // Dia sem intervalo válido ou fora da faixa configurada
  return false
}

/**
 * Determina se o acesso do usuário atual deve ser bloqueado por horário.
 * 
 * @param {object|null} user - Usuário logado
 * @param {Date} [currentDate=new Date()] - Data atual
 * @returns {boolean} true se deve ser bloqueado, false se pode continuar
 */
export function shouldBlockAccess(user, currentDate = new Date()) {
  if (!user || !user.id) return false

  // Administradores (level >= 2) não são bloqueados por horário de acesso
  if ((user.level ?? 0) >= 2) return false

  // Se não tiver access_time configurado, acesso liberado
  if (!user.access_time) return false

  const allowed = isAccessTimeAllowed(user.access_time, currentDate)
  return !allowed
}
